import { useSelector } from '@legendapp/state/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LearningCard, { parseCardContent } from './LearningCard';
import SuggestionButtons from './SuggestionButtons';
import QuizSelection from './QuizSelection';
import CourseExpand from './CourseExpand';
import { cards$, createCard, removeCardByFront } from '../store';

/**
 * Markdown renderer for Mellow AI chat messages.
 * Supports GFM (tables, strikethrough, task lists) and custom mellow:* code blocks.
 *
 * Custom blocks:
 * - mellow:card - Rendered as LearningCard component
 * - mellow:suggestions - Rendered as SuggestionButtons component
 * - mellow:quiz:selection - Rendered as QuizSelection component
 * - mellow:course:* - Rendered as CourseExpand component
 *
 * @param {boolean} disabled - When true, all interactive elements are disabled (for shared view)
 */
export default function MellowMarkdown({ children, chatId, messageIndex, languageCode, initialMode, disabled = false }) {
  // Subscribe to cards items array
  const cardsItems = useSelector(() => cards$.items.get());

  // Check if a card is saved by its front text
  const isCardSaved = (front) => {
    if (!cardsItems || !Array.isArray(cardsItems)) return false;
    return cardsItems.some(
      c => c && c.front?.toLowerCase() === front?.toLowerCase()
    );
  };

  // Handle save/unsave card
  const handleSaveCard = async ({ front, back, cardType, translation, pronunciation }) => {
    if (isCardSaved(front)) {
      // Unsave - remove the card
      await removeCardByFront(front);
    } else {
      // Save - create new card with language, translation, and pronunciation
      await createCard({
        front,
        back,
        cardType,
        translation,
        pronunciation,
        sourceChatId: chatId,
        sourceMessageIndex: messageIndex,
        languageCode,
      });
    }
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code(props) {
          const { children: codeChildren, className, ...rest } = props;

          // Handle mellow:card
          const cardMatch = /language-mellow:card/.exec(className || '');
          if (cardMatch) {
            const cardContent = String(codeChildren).replace(/\n$/, '');
            const parsed = parseCardContent(cardContent);
            const saved = isCardSaved(parsed.title);

            return (
              <LearningCard
                content={cardContent}
                isSaved={saved}
                onSave={handleSaveCard}
                languageCode={languageCode}
                disabled={disabled}
              />
            );
          }

          // Handle mellow:suggestions
          // Format can be:
          // 1. Simple (no type, defaults to 'ask'):
          //    suggestion1
          //    suggestion2
          // 2. With type header (for welcome mode):
          //    type: dict
          //    ---
          //    suggestion1
          //    suggestion2
          const suggestionsMatch = /language-mellow:suggestions/.exec(className || '');
          if (suggestionsMatch) {
            const suggestionsText = String(codeChildren).trim();
            let mode = 'ask'; // default mode
            let suggestionLines = suggestionsText;

            // Check for type: header (used in welcome mode)
            const typeMatch = suggestionsText.match(/^type:\s*(\w+)\s*\n---\n([\s\S]*)$/);
            let hasTypeHeader = false;
            if (typeMatch) {
              mode = typeMatch[1].toLowerCase();
              suggestionLines = typeMatch[2];
              hasTypeHeader = true;
            }

            const suggestions = suggestionLines
              .split('\n')
              .map(line => line.trim())
              .filter(line => line.length > 0);
            // Show header when type is explicitly specified (welcome mode)
            return <SuggestionButtons suggestions={suggestions} mode={mode} showHeader={hasTypeHeader} initialMode={initialMode} messageIndex={messageIndex} disabled={disabled} />;
          }

          // Handle mellow:quiz:selection - always interactive (even in shared view)
          const quizMatch = /language-mellow:quiz:selection/.exec(className || '');
          if (quizMatch) {
            const quizContent = String(codeChildren).replace(/\n$/, '');
            return <QuizSelection content={quizContent} />;
          }

          // Handle mellow:course:prerequisite
          const coursePrereqMatch = /language-mellow:course:prerequisite/.exec(className || '');
          if (coursePrereqMatch) {
            const content = String(codeChildren).replace(/\n$/, '');
            return <CourseExpand content={content} type="prerequisite" disabled={disabled} />;
          }

          // Handle mellow:course:curriculum
          const courseCurrMatch = /language-mellow:course:curriculum/.exec(className || '');
          if (courseCurrMatch) {
            const content = String(codeChildren).replace(/\n$/, '');
            return <CourseExpand content={content} type="curriculum" disabled={disabled} />;
          }

          // Handle mellow:course:configuration
          const courseConfigMatch = /language-mellow:course:configuration/.exec(className || '');
          if (courseConfigMatch) {
            const content = String(codeChildren).replace(/\n$/, '');
            return <CourseExpand content={content} type="configuration" disabled={disabled} />;
          }

          // Regular code (both inline and block)
          // In react-markdown v9+, inline code is NOT wrapped in <pre> by the library
          // Block code IS wrapped in <pre> by the library (handled by pre component below)
          // So this component should just return <code> for both cases
          return <code className={className} {...rest}>{codeChildren}</code>;
        },
        pre(props) {
          const { children: preChildren } = props;
          const className = preChildren?.props?.className || '';

          // Check if this pre contains a mellow:* code block
          // These should be unwrapped (rendered without <pre> wrapper)
          if (className.includes('language-mellow:card') ||
              className.includes('language-mellow:suggestions') ||
              className.includes('language-mellow:quiz:selection')) {
            // Return just the code component (which will render the custom component)
            return preChildren;
          }

          // Normal pre wrapper for block code
          // In react-markdown v9+, only block code gets wrapped in <pre>
          // Inline code never reaches this component
          return <pre {...props} />;
        }
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

